/**
 * Blog search web component.
 * Fetches all blog articles via section rendering, filters client-side,
 * and displays results in the existing grid layout.
 */
class BlogSearch extends HTMLElement {
  static CACHE_DURATION = 5 * 60 * 1000;
  static ARTICLES_PER_PAGE = 12;

  /** @type {HTMLInputElement | null} */
  searchInput;

  connectedCallback() {
    this.searchInput = this.querySelector('[data-blog-search-input]');
    if (!this.searchInput) return;

    this.searchButton = this.querySelector('[data-blog-search-submit]');
    this.clearButton = this.querySelector('[data-blog-search-clear]');
    this.defaultContent = this.querySelector('[data-blog-default-content]');
    this.searchResults = this.querySelector('[data-blog-search-results]');
    this.searchHeader = this.querySelector('[data-blog-search-header]');
    this.searchGrid = this.querySelector('[data-blog-search-grid]');
    this.searchPagination = this.querySelector('[data-blog-search-pagination]');
    this.loadingEl = this.querySelector('[data-blog-search-loading]');

    this.blogUrl = this.dataset.blogUrl || '';
    this.sectionId = this.dataset.sectionId || '';
    this.noResultsText = this.dataset.noResultsText || 'No results found for "{{ terms }}"';
    this.resultsCountText = this.dataset.resultsCountText || '{{ count }} results for "{{ query }}"';

    /** @type {Array<{title: string, text: string, html: string, url: string}> | null} */
    this.articlesCache = null;
    this.cacheTimestamp = 0;
    this.currentSearchTerm = '';
    this.isSearching = false;
    this.searchTimeout = 0;

    this.bindEvents();
  }

  bindEvents() {
    this.searchInput.addEventListener('input', () => {
      clearTimeout(this.searchTimeout);
      this.updateClearButton();
      this.searchTimeout = setTimeout(() => this.handleSearch(), 300);
    });

    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        clearTimeout(this.searchTimeout);
        this.handleSearch();
      }
    });

    this.searchButton?.addEventListener('click', () => {
      clearTimeout(this.searchTimeout);
      this.handleSearch();
    });

    this.clearButton?.addEventListener('click', () => {
      this.clearSearch();
    });
  }

  updateClearButton() {
    if (this.clearButton) {
      this.clearButton.hidden = this.searchInput.value.trim() === '';
    }
  }

  handleSearch() {
    const term = this.searchInput.value.trim();
    if (term === '') {
      this.clearSearch();
      return;
    }
    if (term === this.currentSearchTerm) return;
    this.currentSearchTerm = term;
    this.search(term, 1);
  }

  /**
   * @param {string} term
   * @param {number} page
   */
  async search(term, page = 1) {
    if (this.isSearching) return;
    this.isSearching = true;
    this.showSearchMode();
    this.showLoading();

    try {
      const allArticles = await this.fetchAllArticles();
      const filtered = this.filterArticles(allArticles, term);
      const start = (page - 1) * BlogSearch.ARTICLES_PER_PAGE;
      const paginated = filtered.slice(start, start + BlogSearch.ARTICLES_PER_PAGE);
      this.displayResults(paginated, filtered.length, term, page);
    } catch {
      const fallback = this.getCurrentPageArticles();
      const filtered = this.filterArticles(fallback, term);
      this.displayResults(filtered, filtered.length, term, 1);
    }

    this.isSearching = false;
    this.hideLoading();
  }

  async fetchAllArticles() {
    const now = Date.now();
    if (this.articlesCache && now - this.cacheTimestamp < BlogSearch.CACHE_DURATION) {
      return this.articlesCache;
    }

    const allArticles = [];
    const isTaggedPage = window.location.pathname.includes('/tagged/');

    let totalPages = 1;

    if (!isTaggedPage) {
      allArticles.push(...this.getCurrentPageArticles());
      const grid = this.querySelector('[data-last-page]');
      totalPages = grid ? parseInt(grid.dataset.lastPage, 10) || 1 : 1;
    } else {
      const firstPage = await this.fetchPage(1);
      allArticles.push(...firstPage.articles);
      totalPages = firstPage.totalPages;
    }

    const fetches = [];
    for (let p = isTaggedPage ? 2 : 2; p <= totalPages; p++) {
      fetches.push(
        this.fetchPage(p).then((result) => result.articles).catch(() => [])
      );
    }

    const results = await Promise.all(fetches);
    results.forEach((articles) => allArticles.push(...articles));

    const seen = new Set();
    this.articlesCache = allArticles.filter((a) => {
      if (seen.has(a.url)) return false;
      seen.add(a.url);
      return true;
    });
    this.cacheTimestamp = now;

    return this.articlesCache;
  }

  getCurrentPageArticles() {
    const articles = [];
    const items = this.querySelectorAll('[data-blog-default-content] .blog-post-item');
    items.forEach((item) => {
      const article = this.extractArticle(item);
      if (article) articles.push(article);
    });
    return articles;
  }

  /**
   * @param {number} pageNumber
   * @returns {Promise<{articles: Array, totalPages: number}>}
   */
  async fetchPage(pageNumber) {
    const url = `${this.blogUrl}?section_id=${this.sectionId}&page=${pageNumber}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const articles = [];
    doc.querySelectorAll('.blog-post-item').forEach((item) => {
      const article = this.extractArticle(item);
      if (article) articles.push(article);
    });

    const grid = doc.querySelector('[data-last-page]');
    const totalPages = grid ? parseInt(grid.dataset.lastPage, 10) || 1 : 1;

    return { articles, totalPages };
  }

  /**
   * @param {Element} element
   * @returns {{title: string, text: string, html: string, url: string} | null}
   */
  extractArticle(element) {
    const link = element.querySelector('a[href*="/blogs/"]') || element.querySelector('a');
    if (!link) return null;

    const heading = element.querySelector('h1, h2, h3, h4, h5, h6');
    return {
      title: heading ? heading.textContent.trim() : '',
      url: link.href || link.getAttribute('href') || '',
      text: element.textContent.trim(),
      html: element.outerHTML,
    };
  }

  /**
   * @param {Array} articles
   * @param {string} searchTerm
   */
  filterArticles(articles, searchTerm) {
    const term = searchTerm.toLowerCase();
    return articles.filter(
      (a) => a.title.toLowerCase().includes(term) || a.text.toLowerCase().includes(term)
    );
  }

  /**
   * @param {Array} articles
   * @param {number} totalCount
   * @param {string} searchTerm
   * @param {number} currentPage
   */
  displayResults(articles, totalCount, searchTerm, currentPage) {
    if (!this.searchGrid || !this.searchHeader) return;

    if (totalCount === 0) {
      this.searchHeader.innerHTML = `<p>${this.noResultsText.replace('{{ terms }}', this.escapeHtml(searchTerm))}</p>`;
      this.searchGrid.innerHTML = '';
    } else {
      const countText = this.resultsCountText
        .replace('{{ count }}', String(totalCount))
        .replace('{{ query }}', this.escapeHtml(searchTerm));
      this.searchHeader.innerHTML = `<p>${countText}</p>`;
      this.searchGrid.innerHTML = articles.map((a) => a.html).join('');
    }

    const totalPages = Math.ceil(totalCount / BlogSearch.ARTICLES_PER_PAGE);
    if (totalPages > 1 && this.searchPagination) {
      this.renderPagination(totalPages, currentPage, searchTerm);
      this.searchPagination.hidden = false;
    } else if (this.searchPagination) {
      this.searchPagination.innerHTML = '';
      this.searchPagination.hidden = true;
    }
  }

  /**
   * @param {number} totalPages
   * @param {number} currentPage
   * @param {string} searchTerm
   */
  renderPagination(totalPages, currentPage, searchTerm) {
    let html = '<nav class="pagination" role="navigation" aria-label="Search pagination">';

    if (currentPage > 1) {
      html += `<a href="#" class="pagination__item" data-search-page="${currentPage - 1}" aria-label="Previous">&laquo;</a>`;
    }

    for (let i = 1; i <= totalPages; i++) {
      if (i === currentPage) {
        html += `<span class="pagination__item pagination__item--current" aria-current="page">${i}</span>`;
      } else {
        html += `<a href="#" class="pagination__item" data-search-page="${i}">${i}</a>`;
      }
    }

    if (currentPage < totalPages) {
      html += `<a href="#" class="pagination__item" data-search-page="${currentPage + 1}" aria-label="Next">&raquo;</a>`;
    }

    html += '</nav>';
    this.searchPagination.innerHTML = html;

    this.searchPagination.querySelectorAll('[data-search-page]').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const page = parseInt(/** @type {HTMLElement} */ (link).dataset.searchPage, 10);
        this.isSearching = false;
        this.search(searchTerm, page);
        this.searchResults?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  showSearchMode() {
    if (this.defaultContent) this.defaultContent.hidden = true;
    if (this.searchResults) this.searchResults.hidden = false;
    if (this.clearButton) this.clearButton.hidden = false;
  }

  clearSearch() {
    this.searchInput.value = '';
    this.currentSearchTerm = '';
    if (this.defaultContent) this.defaultContent.hidden = false;
    if (this.searchResults) this.searchResults.hidden = true;
    if (this.clearButton) this.clearButton.hidden = true;
    if (this.searchHeader) this.searchHeader.innerHTML = '';
    if (this.searchGrid) this.searchGrid.innerHTML = '';
    if (this.searchPagination) {
      this.searchPagination.innerHTML = '';
      this.searchPagination.hidden = true;
    }
  }

  showLoading() {
    if (this.loadingEl) this.loadingEl.hidden = false;
    if (this.searchGrid) this.searchGrid.innerHTML = '';
    if (this.searchHeader) this.searchHeader.innerHTML = '';
  }

  hideLoading() {
    if (this.loadingEl) this.loadingEl.hidden = true;
  }

  /**
   * @param {string} str
   * @returns {string}
   */
  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

if (!customElements.get('blog-search')) {
  customElements.define('blog-search', BlogSearch);
}
